import React, { PropsWithChildren } from "react"
import { makeStyles } from "tss-react/mui"
import { Grid, Stack, Typography } from "@mui/material"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../../localization/useDateTime"
import { DigitalSignage } from "../../menuPlanningTypes"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { BatteryStatusChip } from "./BatteryStatusChip"
import { SignsStatusChip } from "./SignsStatusChip"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"

type DigitalSignageDialogProps = Readonly<
    PropsWithChildren<{
        open: boolean
        onClose: () => void
        digitalSignage: DigitalSignage | null
    }>
>

const useStyles = makeStyles()(() => ({
    subtitle: {
        fontWeight: 500,
        fontSize: "1.25rem",
        color: "#666666",
        textAlign: "center",
        marginTop: "-1rem",
    },
    detailsContainer: {
        maxWidth: "25rem",
        padding: "2.5rem",
        margin: "3rem auto 1.5rem auto",
        backgroundColor: tailwindPalette.slate[50],
        borderRadius: ".75rem",
    },
    detailSubtitle: {
        fontSize: "1rem",
        lineHeight: "1.7rem",
        color: tailwindPalette.slate[600],
    },
}))

export function DigitalSignageDialog({ open, onClose, digitalSignage }: DigitalSignageDialogProps) {
    const dateTimeFactory = useDateTime()
    const { l10n } = useAppLocalization()
    const { classes } = useStyles()

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            maxWidthProp="sm"
            dialogTitle={l10n.getString("merchant-menu-planning-digital-signage-dialog-title")}
        >
            <Typography className={classes.subtitle}>{digitalSignage?.name}</Typography>
            <Stack className={classes.detailsContainer} direction="column" spacing="3.75rem">
                {digitalSignage && (
                    <Grid container alignItems="center" spacing={1}>
                        <Grid item xs={6}>
                            <LocalizedStrict id="merchant-menu-planning-digital-signage-card-device-id">
                                <Typography className={classes.detailSubtitle}>Device ID:</Typography>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={6} display="flex" justifyContent="flex-end">
                            <Typography variant="body1" fontWeight="bold">
                                {digitalSignage.id}
                            </Typography>
                        </Grid>

                        <Grid item xs={6}>
                            <LocalizedStrict id="merchant-menu-planning-digital-signage-card-serial-no">
                                <Typography className={classes.detailSubtitle}>Serialno:</Typography>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={6} display="flex" justifyContent="flex-end">
                            <Typography variant="body1" fontWeight="bold">
                                {digitalSignage.serialNumber}
                            </Typography>
                        </Grid>

                        <Grid item xs={6}>
                            <LocalizedStrict id="merchant-menu-planning-digital-signage-card-device-name">
                                <Typography className={classes.detailSubtitle}>Device name:</Typography>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={6} display="flex" justifyContent="flex-end">
                            <Typography variant="body1" fontWeight="bold">
                                {digitalSignage.name}
                            </Typography>
                        </Grid>

                        <Grid item xs={6}>
                            <LocalizedStrict id="merchant-menu-planning-digital-signage-card-last-update">
                                <Typography className={classes.detailSubtitle}>Last updated:</Typography>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={6} display="flex" justifyContent="flex-end">
                            <Typography variant="body1" fontWeight="bold">
                                {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(digitalSignage.lastUpdated))}
                            </Typography>
                        </Grid>
                        <Grid item container alignItems="center" xs={6}>
                            <LocalizedStrict id="merchant-menu-planning-digital-signage-card-battery-status">
                                <Typography className={classes.detailSubtitle}>Battery status:</Typography>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={6} display="flex" justifyContent="flex-end">
                            <BatteryStatusChip batteryLevel={digitalSignage.batteryLevel} />
                        </Grid>

                        <Grid item container alignItems="center" xs={6}>
                            <LocalizedStrict id="merchant-menu-planning-digital-signage-card-status-code">
                                <Typography className={classes.detailSubtitle}>Status code:</Typography>
                            </LocalizedStrict>
                        </Grid>
                        <Grid item xs={6} display="flex" justifyContent="flex-end">
                            <SignsStatusChip status={digitalSignage.statusCode} />
                        </Grid>
                    </Grid>
                )}
            </Stack>
        </StandardDialog>
    )
}
